import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const QUICK_EMOJIS = ['\u{1F44D}', '\u{1F440}', '\u{1F60D}'];
const COMMON_EMOJIS = [
  '\u{1F44D}',
  '\u{1F440}',
  '\u{1F60D}',
  '\u{2764}\u{FE0F}',
  '\u{1F389}',
  '\u{1F680}',
  '\u{1F525}',
  '\u{1F44F}',
  '\u{1F602}',
  '\u{1F923}',
  '\u{1F62E}',
  '\u{1F914}',
  '\u{1F622}',
  '\u{1F60A}',
  '\u{1F4AF}',
  '\u{2705}',
  '\u{1F64C}',
  '\u{1F44C}',
];

interface ReactionPickerProps {
  onSelect: (emoji: string) => void;
  onClose: () => void;
}

export default function ReactionPicker({ onSelect, onClose }: ReactionPickerProps) {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const [showAll, setShowAll] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Measure position from the invisible anchor span (stays in original DOM)
  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    setPos({ top: rect.top, left: rect.left });
  }, []);

  // Recalculate when showAll changes (picker height changes)
  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    setPos({ top: rect.top, left: rect.left });
  }, [showAll]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        pickerRef.current && !pickerRef.current.contains(e.target as Node) &&
        anchorRef.current && !anchorRef.current.parentElement?.contains(e.target as Node)
      ) {
        onClose();
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);
    document.addEventListener('keydown', handleEscape);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const picker = (
    <div
      ref={pickerRef}
      data-reaction-picker
      className="rounded-[var(--radius-md)]"
      style={{
        position: 'fixed',
        zIndex: 9999,
        top: pos ? pos.top : -9999,
        left: pos ? pos.left : 0,
        transform: 'translateY(-100%) translateY(-4px)',
        width: showAll ? 248 : 'auto',
        background: 'var(--rd-surface-panel)',
        border: '1px solid var(--rd-border)',
        boxShadow: 'var(--rd-dropdown-shadow)',
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      {!showAll && (
        <div className="grid grid-cols-4 gap-[6px] p-[var(--sp-2)]">
          {QUICK_EMOJIS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              onClick={() => {
                onSelect(emoji);
                onClose();
              }}
              className="w-[40px] h-[40px] flex items-center justify-center rounded-[var(--radius-sm)] cursor-pointer border-0 bg-transparent text-[20px] transition-colors duration-[120ms]"
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = 'var(--rd-surface-hover)')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = 'transparent')
              }
              aria-label={`React with ${emoji}`}
            >
              {emoji}
            </button>
          ))}

          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="w-[40px] h-[40px] flex items-center justify-center rounded-[var(--radius-sm)] cursor-pointer border-0 bg-transparent transition-colors duration-[120ms]"
            style={{ color: 'var(--rd-text-meta)' }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = 'var(--rd-surface-hover)')
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = 'transparent')
            }
            aria-label="Show more emoji"
            title="More emoji"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <circle cx="5" cy="5" r="1.5" fill="currentColor" />
              <circle cx="11" cy="5" r="1.5" fill="currentColor" />
              <circle cx="5" cy="11" r="1.5" fill="currentColor" />
              <circle cx="11" cy="11" r="1.5" fill="currentColor" />
            </svg>
          </button>
        </div>
      )}

      {showAll && (
        <div
          className="grid grid-cols-5 gap-[6px] p-[var(--sp-2)]"
        >
          {COMMON_EMOJIS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              onClick={() => {
                onSelect(emoji);
                onClose();
              }}
              className="w-[40px] h-[40px] flex items-center justify-center rounded-[var(--radius-sm)] cursor-pointer border-0 bg-transparent text-[20px] transition-colors duration-[120ms]"
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = 'var(--rd-surface-hover)')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = 'transparent')
              }
              aria-label={`React with ${emoji}`}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Invisible anchor stays in the original DOM to measure position */}
      <span ref={anchorRef} style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }} />
      {createPortal(picker, document.body)}
    </>
  );
}
